// @ts-strict-ignore
import {getLocation} from 'common/util/url';

import {reporter} from '../error-reporting';

export const enum UrlFormat {
  Django = `django`, // /report/<projectId>/<appName> or /report/<projectId>/view/<workspaceId>/<appName>
  Volt = `volt`, // /project/<projectId>/app/<appName> or /project/<projectId>/view/<workspaceId>/app/<appName>
}

export interface UrlParsedState {
  urlFormat: UrlFormat;
  projectId: string | null;
  workspaceId: string | null;
  appName: string; // e.g insights, flows (a.k.a tab, reportName, report)
  appPath: string | null; // e.g. /12345 (a.k.a canvas id), /transformations/formulas/4304 (a.k.a lexicon entities)
}

export const StateFromUrlUtils = {
  parseStateFromUrl,
};

/**
 * These apps don't work inside views, we should ignore workspaceId when creating paths for them.
 */
export const PROJECT_ONLY_APPS = new Set([`settings`, `views`]);

/**
 * returns if the appName is a project only app
 */
export function isProjectOnlyApp(appName?: string): boolean {
  return PROJECT_ONLY_APPS.has(appName);
}

/**
 * @returns null if url state can't be inferred from href
 */
export function parseStateFromUrl(pathname: string = null): UrlParsedState | null {
  pathname ||= getLocation().pathname;
  if (/^https?:/.test(pathname)) {
    // full url is passed, extract pathname from it
    pathname = new URL(pathname).pathname;
  }

  // TODO @noj: remove when legacy workspace urls are redirected to new /view based pattern in django
  const djangoWorkspaceMatch = /^\/report\/workspace\/(\d+)\/([a-z-_]+)/.exec(pathname);
  if (djangoWorkspaceMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: null,
      workspaceId: djangoWorkspaceMatch[1],
      appName: djangoWorkspaceMatch[2],
      appPath: null,
    };
  }

  // "triggers" is not a real report/app name, the real name comes after "triggers" in the path: /triggers/[appName]
  const djangoTriggersReportMatch = /^\/report\/(\d+)\/view\/(\d+)\/triggers\/([a-z-_]+)/.exec(pathname);
  if (djangoTriggersReportMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: djangoTriggersReportMatch[1],
      workspaceId: djangoTriggersReportMatch[2],
      appName: djangoTriggersReportMatch[3],
      appPath: null,
    };
  }

  const djangoViewMatch = /^\/report\/(\d+)\/view\/(\d+)\/([a-z-_]+)/.exec(pathname);
  if (djangoViewMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: djangoViewMatch[1],
      workspaceId: djangoViewMatch[2],
      appName: djangoViewMatch[3],
      appPath: null,
    };
  }

  const djangoProjectMatch = /^\/report\/(\d+)\/([a-z-_]+)/.exec(pathname);
  if (djangoProjectMatch) {
    return {
      urlFormat: UrlFormat.Django,
      projectId: djangoProjectMatch[1],
      workspaceId: null,
      appName: djangoProjectMatch[2],
      appPath: null,
    };
  }

  const voltViewMatch = /^\/project\/(\d+)\/view\/(\d+)\/app\/([a-z-]+)(\/.*)?/.exec(pathname);
  if (voltViewMatch) {
    return {
      urlFormat: UrlFormat.Volt,
      projectId: voltViewMatch[1],
      workspaceId: voltViewMatch[2],
      appName: voltViewMatch[3],
      appPath: voltViewMatch[4] || null,
    };
  }

  const voltProjectMatch = /^\/project\/(\d+)\/app\/([a-z-_]+)(\/.*)?/.exec(pathname);
  if (voltProjectMatch) {
    return {
      urlFormat: UrlFormat.Volt,
      projectId: voltProjectMatch[1],
      workspaceId: null,
      appName: voltProjectMatch[2],
      appPath: voltProjectMatch[3] || null,
    };
  }

  const voltAdminOrgSettingsMatch = /^\/organization\/\d+\/app\/settings\/[a-z-_]+/.exec(pathname);
  if (voltAdminOrgSettingsMatch) {
    return {
      urlFormat: UrlFormat.Volt,
      projectId: null,
      workspaceId: null,
      appName: `admin-org-settings`,
      appPath: null,
    };
  }

  return null;
}

/**
 * returns a path like /report/<pid>/<app> or /project/<pid>/view/<wid>/app/<app>
 */
export function makePathFromUrlState(newUrlState: Partial<UrlParsedState>): string {
  const curUrlState = StateFromUrlUtils.parseStateFromUrl();
  let {urlFormat, workspaceId, projectId, appName} = {...curUrlState, ...newUrlState};
  let path = ``;

  if (PROJECT_ONLY_APPS.has(appName)) {
    workspaceId = ``;
  }

  if (urlFormat === UrlFormat.Django) {
    if (workspaceId) {
      if (projectId) {
        path = `/report/${projectId}/view/${workspaceId}/${appName}`;
      } else {
        // legacy url pattern, when we don't know the projectId
        path = `/report/workspace/${workspaceId}/${appName}`;
      }
    } else {
      path = `/report/${projectId}/${appName}`;
    }
  } else if (urlFormat === UrlFormat.Volt) {
    if (workspaceId) {
      path = `/project/${projectId}/view/${workspaceId}/app/${appName}`;
    } else {
      path = `/project/${projectId}/app/${appName}`;
    }
  } else {
    reporter.warn(
      new Error(`makePathFromUrlState cannot create path from urlFormat:${urlFormat}, must be 'django' or 'volt'`),
      urlFormat,
    );
  }

  return path;
}
